import type { CbiFrontendServiceContext } from "../../frontend/context/CbiFrontendServiceContext";
import type { ApClassSignupRef } from "./ApClassSignupRef";
import type { BalanceTransactionStaff } from "./BalanceTransactionStaff";
import type { CardStaff } from "./CardStaff";
import type { CatalogItemInstanceRef } from "./CatalogItemInstanceRef";
import type { CatalogItemInstanceStaff } from "./CatalogItemInstanceStaff";
import type { ConfigureDelivery } from "./ConfigureDelivery";
import type { DamageWaiverStaff } from "./DamageWaiverStaff";
import type { DonationStaff } from "./DonationStaff";
import type { GuestPrivilegeStaff } from "./GuestPrivilegeStaff";
import type { LineItemAmountsBase } from "./LineItemAmountsBase";
import type { LineItemDTOBase } from "./LineItemDTOBase";
import type { LineItemDiscountInstanceRef } from "./LineItemDiscountInstanceRef";
import type { LineItemDiscountInstanceStaff } from "./LineItemDiscountInstanceStaff";
import type { LineItemRefundStaff } from "./LineItemRefundStaff";
import type { LineItemTaxStaff } from "./LineItemTaxStaff";
import type { MembershipStaff } from "./MembershipStaff";
import type { OrderStaff } from "./OrderStaff";
import type { PersonStaff } from "./PersonStaff";
import type { PurchaseDeliveryRef } from "./PurchaseDeliveryRef";
import type { PurchaseDeliveryStaff } from "./PurchaseDeliveryStaff";
import type { StockChangeStaff } from "./StockChangeStaff";
import type { TrackedModernBase } from "./TrackedModernBase";

export class LineItemStaff implements LineItemDTOBase, TrackedModernBase, LineItemAmountsBase {
  constructor(public readonly assignId: number, public readonly referenceId: string | null, public readonly amountSpecifiedCents: number | null, public readonly amountPaidCents: number | null, public readonly personId: PersonStaff | null, public readonly displayOrder: number | null, public readonly autoAdded: boolean | null, public readonly squareLineItemUid: string | null, public readonly expiresOn: Date | null, public readonly donation: DonationStaff | null, public readonly membership: MembershipStaff | null, public readonly balanceTransaction: BalanceTransactionStaff | null, public readonly stockChange: StockChangeStaff | null, public readonly card: CardStaff | null, public readonly guestPrivs: GuestPrivilegeStaff | null, public readonly damageWaiver: DamageWaiverStaff | null, public readonly classSignup: ApClassSignupRef | null, public readonly subscriptionId: number | null, public readonly purchaseDelivery: PurchaseDeliveryStaff | null, public readonly buyerName: string | null, public readonly buyerIsPurchaser: boolean | null, public readonly createdOn: Date | null, public readonly createdBy: string | null, public readonly updatedOn: Date | null, public readonly updatedBy: string | null, public readonly quantity: number | null, public readonly amountBaseCents: number | null, public readonly amountSubtotalCents: number | null, public readonly amountDiscountsBaseCents: number | null, public readonly amountDiscountsTotalCents: number | null, public readonly amountTaxesBaseCents: number | null, public readonly amountTaxesTotalCents: number | null, public readonly amountRefundedCents: number | null, public readonly quantityRefunded: number | null, public readonly amountToRefundCents: number | null, public readonly amountTotalCents: number | null, public readonly orderId: OrderStaff | null, public readonly itemInstanceId: CatalogItemInstanceStaff | null, public readonly parentItemId: LineItemStaff | null, public readonly childItems: Array<LineItemStaff> | null, public readonly discountInstances: Array<LineItemDiscountInstanceStaff> | null, public readonly taxes: Array<LineItemTaxStaff> | null, public readonly refunds: Array<LineItemRefundStaff> | null, public readonly itemInstanceIdTargetIds: CatalogItemInstanceRef) {
    this.cbiServiceContext = null;
    this.personIdLoaded = this.personId != null;
    this.personIdCached = this.personId;
    this.donationLoaded = this.donation != null;
    this.donationCached = this.donation;
    this.membershipLoaded = this.membership != null;
    this.membershipCached = this.membership;
    this.balanceTransactionLoaded = this.balanceTransaction != null;
    this.balanceTransactionCached = this.balanceTransaction;
    this.stockChangeLoaded = this.stockChange != null;
    this.stockChangeCached = this.stockChange;
    this.cardLoaded = this.card != null;
    this.cardCached = this.card;
    this.guestPrivsLoaded = this.guestPrivs != null;
    this.guestPrivsCached = this.guestPrivs;
    this.damageWaiverLoaded = this.damageWaiver != null;
    this.damageWaiverCached = this.damageWaiver;
    this.purchaseDeliveryLoaded = this.purchaseDelivery != null;
    this.purchaseDeliveryCached = this.purchaseDelivery;
    this.orderIdLoaded = this.orderId != null;
    this.orderIdCached = this.orderId;
    this.itemInstanceIdLoaded = this.itemInstanceId != null;
    this.itemInstanceIdCached = this.itemInstanceId;
    this.parentItemIdLoaded = this.parentItemId != null;
    this.parentItemIdCached = this.parentItemId;
    this.childItemsLoaded = this.childItems != null;
    this.childItemsCached = this.childItems;
    this.discountInstancesLoaded = this.discountInstances != null;
    this.discountInstancesCached = this.discountInstances;
    this.taxesLoaded = this.taxes != null;
    this.taxesCached = this.taxes;
    this.refundsLoaded = this.refunds != null;
    this.refundsCached = this.refunds;
  }
  cbiServiceContext: CbiFrontendServiceContext | null;
  private personIdLoaded: boolean;
  private personIdCached: PersonStaff | null;
  private donationLoaded: boolean;
  private donationCached: DonationStaff | null;
  private membershipLoaded: boolean;
  private membershipCached: MembershipStaff | null;
  private balanceTransactionLoaded: boolean;
  private balanceTransactionCached: BalanceTransactionStaff | null;
  private stockChangeLoaded: boolean;
  private stockChangeCached: StockChangeStaff | null;
  private cardLoaded: boolean;
  private cardCached: CardStaff | null;
  private guestPrivsLoaded: boolean;
  private guestPrivsCached: GuestPrivilegeStaff | null;
  private damageWaiverLoaded: boolean;
  private damageWaiverCached: DamageWaiverStaff | null;
  private purchaseDeliveryLoaded: boolean;
  private purchaseDeliveryCached: PurchaseDeliveryStaff | null;
  private orderIdLoaded: boolean;
  private orderIdCached: OrderStaff | null;
  private itemInstanceIdLoaded: boolean;
  private itemInstanceIdCached: CatalogItemInstanceStaff | null;
  private parentItemIdLoaded: boolean;
  private parentItemIdCached: LineItemStaff | null;
  private childItemsLoaded: boolean;
  private childItemsCached: Array<LineItemStaff> | null;
  private discountInstancesLoaded: boolean;
  private discountInstancesCached: Array<LineItemDiscountInstanceStaff> | null;
  private taxesLoaded: boolean;
  private taxesCached: Array<LineItemTaxStaff> | null;
  private refundsLoaded: boolean;
  private refundsCached: Array<LineItemRefundStaff> | null;
  async personIdLazy(): Promise<PersonStaff | null> {
    if (!this.personIdLoaded) {
      this.personIdCached = await this.cbiServiceContext!.orderService().getLineItemStaffPersonId(this.assignId);
      this.personIdLoaded = true;
    }
    return this.personIdCached;
  }
  async donationLazy(): Promise<DonationStaff | null> {
    if (!this.donationLoaded) {
      this.donationCached = await this.cbiServiceContext!.orderService().getLineItemStaffDonation(this.assignId);
      this.donationLoaded = true;
    }
    return this.donationCached;
  }
  async membershipLazy(): Promise<MembershipStaff | null> {
    if (!this.membershipLoaded) {
      this.membershipCached = await this.cbiServiceContext!.orderService().getLineItemStaffMembership(this.assignId);
      this.membershipLoaded = true;
    }
    return this.membershipCached;
  }
  async balanceTransactionLazy(): Promise<BalanceTransactionStaff | null> {
    if (!this.balanceTransactionLoaded) {
      this.balanceTransactionCached = await this.cbiServiceContext!.orderService().getLineItemStaffBalanceTransaction(this.assignId);
      this.balanceTransactionLoaded = true;
    }
    return this.balanceTransactionCached;
  }
  async stockChangeLazy(): Promise<StockChangeStaff | null> {
    if (!this.stockChangeLoaded) {
      this.stockChangeCached = await this.cbiServiceContext!.orderService().getLineItemStaffStockChange(this.assignId);
      this.stockChangeLoaded = true;
    }
    return this.stockChangeCached;
  }
  async cardLazy(): Promise<CardStaff | null> {
    if (!this.cardLoaded) {
      this.cardCached = await this.cbiServiceContext!.orderService().getLineItemStaffCard(this.assignId);
      this.cardLoaded = true;
    }
    return this.cardCached;
  }
  async guestPrivsLazy(): Promise<GuestPrivilegeStaff | null> {
    if (!this.guestPrivsLoaded) {
      this.guestPrivsCached = await this.cbiServiceContext!.orderService().getLineItemStaffGuestPrivs(this.assignId);
      this.guestPrivsLoaded = true;
    }
    return this.guestPrivsCached;
  }
  async damageWaiverLazy(): Promise<DamageWaiverStaff | null> {
    if (!this.damageWaiverLoaded) {
      this.damageWaiverCached = await this.cbiServiceContext!.orderService().getLineItemStaffDamageWaiver(this.assignId);
      this.damageWaiverLoaded = true;
    }
    return this.damageWaiverCached;
  }
  async purchaseDeliveryLazy(): Promise<PurchaseDeliveryStaff | null> {
    if (!this.purchaseDeliveryLoaded) {
      this.purchaseDeliveryCached = await this.cbiServiceContext!.orderService().getLineItemStaffPurchaseDelivery(this.assignId);
      this.purchaseDeliveryLoaded = true;
    }
    return this.purchaseDeliveryCached;
  }
  async orderIdLazy(): Promise<OrderStaff> {
    if (!this.orderIdLoaded) {
      this.orderIdCached = await this.cbiServiceContext!.orderService().getLineItemStaffOrderId(this.assignId);
      this.orderIdLoaded = true;
    }
    return this.orderIdCached!;
  }
  async itemInstanceIdLazy(): Promise<CatalogItemInstanceStaff> {
    if (!this.itemInstanceIdLoaded) {
      this.itemInstanceIdCached = await this.cbiServiceContext!.catalogService().getCatalogItemInstanceStaff(this.itemInstanceIdTargetIds.assignId);
      this.itemInstanceIdLoaded = true;
    }
    return this.itemInstanceIdCached!;
  }
  async parentItemIdLazy(): Promise<LineItemStaff | null> {
    if (!this.parentItemIdLoaded) {
      this.parentItemIdCached = await this.cbiServiceContext!.orderService().getLineItemStaffParentItemId(this.assignId);
      this.parentItemIdLoaded = true;
    }
    return this.parentItemIdCached;
  }
  async childItemsLazy(): Promise<Array<LineItemStaff>> {
    if (!this.childItemsLoaded) {
      this.childItemsCached = await this.cbiServiceContext!.orderService().getLineItemStaffChildItems(this.assignId);
      this.childItemsLoaded = true;
    }
    return this.childItemsCached!;
  }
  async discountInstancesLazy(): Promise<Array<LineItemDiscountInstanceStaff>> {
    if (!this.discountInstancesLoaded) {
      this.discountInstancesCached = await this.cbiServiceContext!.orderService().getLineItemStaffDiscountInstances(this.assignId);
      this.discountInstancesLoaded = true;
    }
    return this.discountInstancesCached!;
  }
  async taxesLazy(): Promise<Array<LineItemTaxStaff>> {
    if (!this.taxesLoaded) {
      this.taxesCached = await this.cbiServiceContext!.orderService().getLineItemStaffTaxes(this.assignId);
      this.taxesLoaded = true;
    }
    return this.taxesCached!;
  }
  async refundsLazy(): Promise<Array<LineItemRefundStaff>> {
    if (!this.refundsLoaded) {
      this.refundsCached = await this.cbiServiceContext!.orderService().getLineItemStaffRefunds(this.assignId);
      this.refundsLoaded = true;
    }
    return this.refundsCached!;
  }
  async configureDelivery(request: ConfigureDelivery): Promise<PurchaseDeliveryRef> {
    return await this.cbiServiceContext!.orderService().configureDeliveryStaff({ lineItem: { assignId: this.assignId }, request: request });
  }
  async confirmDelivery(): Promise<void> {
    return await this.cbiServiceContext!.orderService().confirmDeliveryStaff({ assignId: this.assignId });
  }
  async removeFromCart(): Promise<void> {
    return await this.cbiServiceContext!.orderService().removeFromCartStaff({ assignId: this.assignId });
  }
  async unapplyLineItemDiscount(discount: LineItemDiscountInstanceRef): Promise<void> {
    return await this.cbiServiceContext!.orderService().unapplyLineItemDiscountStaff({ lineItem: { assignId: this.assignId }, discount: discount });
  }
}
